import {
  Body,
  Box,
  type BoxProps,
  Center,
  type IntrinsicSystemProps,
  VStack,
  forwardRef,
  mapResponsive,
  motion,
  system,
  transition,
} from '@hover/blueprint';
import React from 'react';

import { Skeleton } from 'src/shared/components/Skeleton/Skeleton';
import type { SkeletonProps } from 'src/shared/components/Skeleton/Skeleton';
import { fadePresence } from 'src/shared/utils/motion';

const MotionCenter = motion(Center);

type DivProps = IntrinsicSystemProps<'div'>;

interface SimpleTileProps extends BoxProps, Pick<SkeletonProps, 'colorScheme'> {
  text: string;
  onClick?: () => void;
  tileProps?: DivProps;
}

export const SimpleTile = forwardRef<SimpleTileProps, 'div'>(
  ({ text, colorScheme, onClick, children, tileProps, ...props }, ref) => (
    <Box
      borderRadius="300"
      cursor="pointer"
      onClick={onClick}
      position="relative"
      {...props}
      ref={ref}
    >
      <VStack
        alignItems="start"
        borderRadius="inherit"
        height="100%"
        width="100%"
      >
        <Skeleton
          borderRadius="inherit"
          colorScheme={colorScheme}
          flexShrink={0}
          isLoaded
          minWidth={1}
          sx={{ aspectRatio: '1/1' }}
          width={1}
        >
          <system.div
            borderRadius="inherit"
            boxSizing="border-box"
            height={1}
            outline="1px solid"
            outlineColor="neutral.300"
            outlineOffset="2px"
            p={200}
            position="relative"
            width={1}
            {...transition.easing250('outline-color')}
            {...tileProps}
          >
            <MotionCenter
              bg="neutral.0"
              borderRadius="round"
              height="300"
              inset={0}
              margin="auto"
              position="absolute"
              shadow="distance400"
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              width="300"
              {...fadePresence}
            >
              {children}
            </MotionCenter>
          </system.div>
        </Skeleton>
        <Skeleton colorScheme={colorScheme} isLoaded width="100%">
          <Body
            color={mapResponsive(
              c => (c === 'light' ? 'neutral.0' : 'neutral.900'),
              colorScheme,
            )}
            isSemiBold
            m={0}
            noOfLines={2}
            pl="100"
          >
            {text}
          </Body>
        </Skeleton>
      </VStack>
    </Box>
  ),
);
